import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Shield, Plus, Edit, Trash2, Users, CheckCircle, AlertCircle, Copy } from 'lucide-react';

export default function RBACManagement() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloningRole, setCloningRole] = useState(null);
  const [cloneName, setCloneName] = useState('');
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#6b7280',
    permissions: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes, usersRes] = await Promise.all([
        api.get('/admin/rbac/roles'),
        api.get('/admin/rbac/permissions'),
        api.get('/admin/users?role=admin&limit=500')
      ]);
      setRoles(rolesRes.data.roles || []);
      setPermissions(permsRes.data.permissions || {});
      setAdminUsers(usersRes.data.users || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load RBAC data');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const res = await api.post('/admin/rbac/roles/seed-defaults');
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error('Failed to seed default roles');
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Role name is required');
      return;
    }

    if (formData.permissions.length === 0) {
      toast.error('Please select at least one permission');
      return;
    }

    try {
      const params = new URLSearchParams({
        name: formData.name,
        description: formData.description,
        color: formData.color,
        permissions: formData.permissions.join(',')
      });

      if (editingRole) {
        await api.put(`/admin/rbac/roles/${editingRole.id}?${params}`);
        toast.success('Role updated successfully');
      } else {
        await api.post(`/admin/rbac/roles/create?${params}`);
        toast.success('Role created successfully');
      }

      setCreateDialogOpen(false);
      setEditingRole(null);
      setFormData({ name: '', description: '', color: '#6b7280', permissions: [] });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      color: role.color || '#6b7280',
      permissions: role.permissions || []
    });
    setCreateDialogOpen(true);
  };

  const handleDelete = async (roleId, roleName) => {
    if (!confirm(`Are you sure you want to delete the role "${roleName}"?`)) return;

    try {
      await api.delete(`/admin/rbac/roles/${roleId}`);
      toast.success('Role deleted successfully');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete role');
    }
  };

  const togglePermission = (permKey) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permKey)
        ? prev.permissions.filter(p => p !== permKey)
        : [...prev.permissions, permKey]
    }));
  };

  const toggleCategoryAll = (category) => {
    const categoryPerms = Object.keys(permissions[category].permissions);
    const allSelected = categoryPerms.every(p => formData.permissions.includes(p));

    setFormData(prev => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter(p => !categoryPerms.includes(p))
        : [...new Set([...prev.permissions, ...categoryPerms])]
    }));
  };

  const handleCloneRole = async () => {
    if (!cloneName.trim()) {
      toast.error('Clone name is required');
      return;
    }

    try {
      const params = new URLSearchParams({ new_name: cloneName });
      await api.post(`/admin/rbac/roles/${cloningRole.id}/clone?${params}`);
      toast.success(`Role cloned as "${cloneName}"`);
      setCloneDialogOpen(false);
      setCloningRole(null);
      setCloneName('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clone role');
    }
  };

  const handleBulkAssign = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    try {
      const params = new URLSearchParams({
        user_ids: selectedUsers.join(','),
        role_id: selectedRole.id
      });
      const res = await api.post(`/admin/rbac/bulk-assign?${params}`);
      toast.success(res.data.message);
      setBulkAssignOpen(false);
      setSelectedUsers([]);
      setSelectedRole(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign role');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">
            RBAC Management
          </h1>
          <p className="text-muted-foreground mt-1">Manage roles and permissions for admin users</p>
        </div>
        <div className="flex gap-2">
          {roles.length === 0 && (
            <Button variant="outline" onClick={handleSeedDefaults}>
              <Shield className="h-4 w-4 mr-2" />
              Seed Default Roles
            </Button>
          )}
          <Button onClick={() => { setEditingRole(null); setFormData({ name: '', description: '', color: '#6b7280', permissions: [] }); setCreateDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Create Custom Role
          </Button>
        </div>
      </div>

      {roles.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Roles Configured</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Start by seeding default role templates or create a custom role
            </p>
            <Button onClick={handleSeedDefaults}>
              Seed Default Role Templates
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map(role => (
            <Card key={role.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: role.color }}
                    />
                    <CardTitle className="text-base">{role.name}</CardTitle>
                  </div>
                  <div className="flex gap-1">
                    {role.is_default && (
                      <Badge variant="secondary" className="text-xs">Template</Badge>
                    )}
                    {role.is_system && (
                      <Badge className="text-xs bg-red-100 text-red-800">System</Badge>
                    )}
                  </div>
                </div>
                <CardDescription className="text-xs mt-1">
                  {role.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                      <span className="font-medium">{role.permissions?.length || 0} permissions</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Users className="h-3 w-3" />
                      <span>{role.user_count || 0} users</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1">
                    {(role.permissions || []).slice(0, 5).map(perm => (
                      <Badge key={perm} variant="outline" className="text-xs">
                        {perm.split('.')[0]}
                      </Badge>
                    ))}
                    {role.permissions?.length > 5 && (
                      <Badge variant="outline" className="text-xs">
                        +{role.permissions.length - 5} more
                      </Badge>
                    )}
                  </div>

                  <Separator />

                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleEdit(role)}
                      disabled={role.is_system}
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => { setCloningRole(role); setCloneName(`${role.name} Copy`); setCloneDialogOpen(true); }}
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Clone
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => { setSelectedRole(role); setBulkAssignOpen(true); }}
                    >
                      <Users className="h-3 w-3 mr-1" />
                      Assign
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive" 
                      onClick={() => handleDelete(role.id, role.name)}
                      disabled={role.is_system}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Role Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit Role' : 'Create Custom Role'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role Name *</Label>
                <Input
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  placeholder="Marketing Manager"
                />
              </div>

              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={formData.color}
                    onChange={e => setFormData({...formData, color: e.target.value})}
                    className="w-20"
                  />
                  <Input
                    value={formData.color}
                    onChange={e => setFormData({...formData, color: e.target.value})}
                    placeholder="#6b7280"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={e => setFormData({...formData, description: e.target.value})}
                placeholder="Describe this role's purpose and responsibilities..."
                rows={2}
              />
            </div>

            <Separator />

            <div>
              <h3 className="font-medium mb-3">Permissions ({formData.permissions.length} selected)</h3>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Select the permissions this role should have. Click category headers to select all.
                </AlertDescription>
              </Alert>

              <div className="mt-4 space-y-4">
                {Object.entries(permissions).map(([categoryKey, category]) => {
                  const categoryPerms = Object.keys(category.permissions);
                  const selectedCount = categoryPerms.filter(p => formData.permissions.includes(p)).length;
                  const allSelected = selectedCount === categoryPerms.length;

                  return (
                    <Card key={categoryKey}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={allSelected}
                              onCheckedChange={() => toggleCategoryAll(categoryKey)}
                            />
                            <CardTitle className="text-sm">{category.label}</CardTitle>
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {selectedCount}/{categoryPerms.length}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-2">
                          {Object.entries(category.permissions).map(([permKey, permLabel]) => (
                            <div key={permKey} className="flex items-center space-x-2">
                              <Checkbox
                                id={permKey}
                                checked={formData.permissions.includes(permKey)}
                                onCheckedChange={() => togglePermission(permKey)}
                              />
                              <label
                                htmlFor={permKey}
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                              >
                                {permLabel}
                              </label>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {editingRole ? 'Update Role' : 'Create Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clone Role Dialog */}
      <Dialog open={cloneDialogOpen} onOpenChange={setCloneDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clone Role: {cloningRole?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New Role Name *</Label>
              <Input
                value={cloneName}
                onChange={e => setCloneName(e.target.value)}
                placeholder="Enter new role name..."
              />
            </div>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                This will create a new role with the same permissions as "{cloningRole?.name}".
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloneDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCloneRole}>
              <Copy className="h-4 w-4 mr-2" />
              Clone Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={bulkAssignOpen} onOpenChange={setBulkAssignOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Assign Role: {selectedRole?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Alert>
              <Users className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Select admin users to assign the role "{selectedRole?.name}" ({selectedUsers.length} selected)
              </AlertDescription>
            </Alert>
            
            <div className="space-y-2 max-h-96 overflow-y-auto border rounded-md p-3">
              {adminUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No admin users found</p>
              ) : (
                adminUsers.map(user => (
                  <div key={user.id} className="flex items-center space-x-2 p-2 hover:bg-accent rounded">
                    <Checkbox
                      id={`user-${user.id}`}
                      checked={selectedUsers.includes(user.id)}
                      onCheckedChange={() => toggleUserSelection(user.id)}
                    />
                    <label
                      htmlFor={`user-${user.id}`}
                      className="flex-1 cursor-pointer text-sm"
                    >
                      <div className="font-medium">{user.name}</div>
                      <div className="text-xs text-muted-foreground">{user.email}</div>
                      {user.rbac_role_name && (
                        <Badge variant="outline" className="text-xs mt-1">
                          Current: {user.rbac_role_name}
                        </Badge>
                      )}
                    </label>
                  </div>
                ))
              )}
            </div>

            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedUsers(adminUsers.map(u => u.id))}
              >
                Select All
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedUsers([])}
              >
                Clear Selection
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAssignOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkAssign} disabled={selectedUsers.length === 0}>
              Assign to {selectedUsers.length} User{selectedUsers.length !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
